import React, { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ReceiptPrintTemplate,printReceipt as printReceiptShared } from "../../utils/printReceipt";

const Card = ({ data, admin, onDelete, time, role, userName }) => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState(null);
  const receiptRef = useRef(null);
  const normalizedReceipt = selectedItem ? { ...selectedItem, userName: userName } : null;

  const totalWeight = data.reduce((acc, item) => acc + parseFloat(item.weight || 0), 0);
  const totalMoney = data.reduce(
    (acc, item) => acc + parseFloat(String(item.total).replace(/[^0-9.-]+/g, "") || 0),
    0
  );

  const formatFat = (fat) => (fat > 20 ? `${(fat / 10).toFixed(1)}%` : `${fat}%`);

  const handleDeleteClick = () => {
    if (selectedItem) {
      onDelete(selectedItem.id, time);
      setSelectedItem(null);
    }
  };

  const DetailRow = ({ label, value, highlight }) => (
    <div className="flex justify-between items-center py-3 border-b border-line last:border-0">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">{label}</span>
      <span className={`font-bold ${highlight ? "text-credit font-mono text-[20px]" : "text-primary text-[14px]"}`}>
        {value}
      </span>
    </div>
  );

const handlePrintEntry = () => {
  printReceiptShared({
    receiptData: {
      ...selectedItem,
      userName: userName,
    },
    receiptRef,
    t,
  });
};

const HiddenReceipt = () => (
  <div
    ref={receiptRef}
    className="absolute left-[-9999px] top-0 w-[300px] bg-white p-4"
    style={{ fontFamily: "'Courier New', monospace", color: "#000" }}
  >
    <div className="text-center font-bold text-lg mb-2 pb-1 border-b border-dashed border-gray-800">
      {t("receipt.title")}
    </div>
    <div className="mb-3 space-y-1">
      <div className="flex justify-between">
        <span>{t("receipt.date")}:</span>
        <span>{selectedItem?.date}</span>
      </div>
      <div className="flex justify-between">
        <span>{t("receipt.user")}:</span>
        <span>{userName}</span>
      </div>
    </div>
    <div className="border-t border-dashed border-gray-800 my-2"></div>
    <div className="mb-3 space-y-1">
      <div className="flex justify-between">
        <span>{t("card.labels.animal")}:</span>
        <span>{selectedItem?.animalType || "-"}</span>
      </div>
      <div className="flex justify-between">
        <span>{t("card.labels.calc_type")}:</span>
        <span>
          {selectedItem?.snf ? t("receipt.fat_snf") : t("receipt.fat_only")}
        </span>
      </div>
      <div className="flex justify-between">
        <span>{t("receipt.weight")}:</span>
        <span>
          {selectedItem?.weight} {t("card.ltr")}
        </span>
      </div>
      <div className="flex justify-between">
        <span>{t("receipt.fat")}:</span>
        <span>{selectedItem?.fat}%</span>
      </div>
      {selectedItem?.snf && (
        <div className="flex justify-between">
          <span>{t("receipt.snf")}:</span>
          <span>{selectedItem?.snf}%</span>
        </div>
      )}
      <div className="flex justify-between">
        <span>{t("receipt.rate")}:</span>
        <span>₹{selectedItem?.price}</span>
      </div>
    </div>
    <div className="border-t border-dashed border-gray-800 my-2"></div>
    <div className="flex justify-between font-bold text-base">
      <span>{t("receipt.total")}:</span>
      <span>₹{selectedItem?.total}</span>
    </div>
    <div className="text-center text-xs mt-4 text-gray-600">
      {t("receipt.generated")} {new Date().toLocaleString()}
    </div>
  </div>
);

  return (
    <div className="w-full mb-10">
      {selectedItem && <ReceiptPrintTemplate ref={receiptRef} receiptData={normalizedReceipt} t={t} />}

      {data.length > 0 ? (
        <div className="bg-white border border-line rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[24%]" />
              <col className="w-[18%]" />
              <col className="w-[18%]" />
              <col className="w-[18%]" />
            </colgroup>
            <thead>
              <tr className="bg-paper/50 border-b border-line">
                <th className="text-left font-bold text-primary text-[9px] sm:text-[10px] uppercase tracking-widest py-3 px-2 sm:px-3">
                  {t("card.labels.date")}
                </th>
                <th className="text-right font-bold text-primary text-[9px] sm:text-[10px] uppercase tracking-widest py-3 px-2 sm:px-3">
                  {t("card.labels.weight")}
                </th>
                <th className="text-right font-bold text-primary text-[9px] sm:text-[10px] uppercase tracking-widest py-3 px-2 sm:px-3">
                  {t("card.labels.fat")}
                </th>
                <th className="text-right font-bold text-primary text-[9px] sm:text-[10px] uppercase tracking-widest py-3 px-2 sm:px-3">
                  {t("card.labels.price")}
                </th>
                <th className="text-right font-bold text-primary text-[9px] sm:text-[10px] uppercase tracking-widest py-3 px-2 sm:px-3">
                  {t("card.labels.total")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={item.id ?? index}
                  onClick={() => setSelectedItem(item)}
                  className={`cursor-pointer transition-colors hover:bg-primary/5 ${
                    index % 2 === 1 ? "bg-paper/30" : "bg-white"
                  }`}
                >
                  <td className="py-3 px-2 sm:px-3 font-bold text-primary text-[11px] sm:text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis">
                    {item.date}
                  </td>
                  <td className="py-3 px-2 sm:px-3 text-right font-medium text-primary text-[11px] sm:text-[12.5px] whitespace-nowrap">
                    {item.weight} <span className="text-[9px] sm:text-[10px] text-[#6B7280]">{t("card.ltr")}</span>
                  </td>
                  <td className="py-3 px-2 sm:px-3 text-right font-medium text-primary text-[11px] sm:text-[12.5px] whitespace-nowrap">
                    {formatFat(item.fat)}
                  </td>
                  <td className="py-3 px-2 sm:px-3 text-right font-medium text-[#6B7280] text-[11px] sm:text-[12.5px] whitespace-nowrap">
                    ₹ {item.price}
                  </td>
                  <td className="py-3 px-2 sm:px-3 text-right font-bold font-mono text-primary text-[12px] sm:text-[13.5px] whitespace-nowrap">
                    ₹ {item.total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex w-full justify-center h-[100px] items-center text-[#6B7280] text-[13px] font-medium">
          {t("card.empty")}
        </div>
      )}

      {/* Summary Footer */}
      {data.length > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between sm:items-center gap-3 p-4 bg-paper/50 border border-line rounded-2xl shadow-sm">
          <span className="text-[10px] font-bold text-primary uppercase tracking-widest">
            {t("card.total_label", "Total")} · {data.length} {t("card.entries_label", "entries")}
          </span>
          <div className="flex items-baseline justify-between sm:justify-end gap-6 w-full sm:w-auto">
            <span className="text-primary font-bold text-[14px]">
              {totalWeight.toFixed(2)} <span className="text-[11px] text-[#6B7280]">{t("card.ltr")}</span>
            </span>
            <span
              className={`font-bold font-mono text-[18px] whitespace-nowrap ${
                totalMoney >= 0 ? "text-credit" : "text-debit"
              }`}
            >
              ₹ {totalMoney.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white border border-line rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all duration-200">
            
            {/* Modal Header */}
            <div className="bg-paper/80 px-5 py-4 border-b border-line flex justify-between items-center">
              <h3 className="font-bold text-[15px] text-primary flex items-center gap-2 tracking-wide">
                {t("card.actions.details")}
              </h3>
              <button onClick={() => setSelectedItem(null)} className="p-1 text-[#6B7280] hover:text-primary transition-colors bg-white rounded-full border border-line shadow-sm" aria-label={t("card.actions.close")}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-5 py-2">
              <div className="grid grid-cols-1">
                <DetailRow label={t("card.labels.date")} value={selectedItem.date} />
                <DetailRow label={t("card.labels.weight")} value={`${selectedItem.weight} ${t("card.ltr")}`} />
                <DetailRow label={t("card.labels.fat")} value={formatFat(selectedItem.fat)} />
                <DetailRow label={t("card.labels.price")} value={`₹ ${selectedItem.price}`} />
                <DetailRow label={t("card.labels.total")} value={`₹ ${selectedItem.total}`} highlight={true} />

                {selectedItem.time && <DetailRow label={t("card.labels.time")} value={selectedItem.time} />}
                {selectedItem.animalType && (
                  <DetailRow label={t("card.labels.animal")} value={selectedItem.animalType} />
                )}
              </div>
            </div>

            {/* Modal Footer (Actions) */}
            <div className="bg-paper/50 px-5 py-4 border-t border-line flex gap-3 justify-end">
              <button
                onClick={handlePrintEntry}
                className="px-4 py-2 text-[12px] font-bold text-primary bg-white border border-line shadow-sm rounded-lg hover:border-primary/50 transition-colors flex-1"
              >
                {t("card.actions.print")}
              </button>

              {role !== "associated user" && (
                <button
                  className="px-4 py-2 text-[12px] font-bold bg-debit/10 text-debit border border-debit/20 rounded-lg hover:bg-debit/20 transition-colors flex-1"
                  onClick={handleDeleteClick}
                >
                  {t("card.actions.delete")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;
